import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  type NCRColumnConfig,
  loadNCRColumns,
  customerReferenceLabel,
  renderNCRCell,
} from '../components/quality/qualityColumnConfig';
import { QualityColumnsDropdown } from '../components/quality/QualityColumnsDropdown';
import NCRQuickFilters, { NCR_QUICK_MODES, type NCRQuickMode } from '../components/quality/NCRQuickFilters';
import NCRRowActions from '../components/quality/NCRRowActions';
import { PlateLegend } from '../components/quality/ncrPlate';
import * as XLSX from 'xlsx';
import { companyNcrReference, capitaliseSeverity } from '../lib/ncrReference';
import NCRFiltersBar, {
  LIABILITY_OPTIONS,
  CLAIM_STATUS_OPTIONS,
  NCR_STATUS_OPTIONS,
  getRollingTwelveMonthsRange,
} from '../components/quality/NCRFiltersBar';
import { buildNCRPanelParams, countActiveNCRFilters, type NCRFiltersState } from '../components/quality/ncrFilterParams';
import { useNCRFilterDraft } from '../components/quality/useNCRFilterDraft';
import { useMyCollectionsCount } from '../components/quality/useMyCollectionsCount';
import { QualityTabStrip } from '../components/quality/QualityTabStrip';
import ColumnHeaderFilter, { type ColumnFilterDescriptor } from '../components/quality/ColumnHeaderFilter';
// CR-5887 (TEMPORARY — remove after testing): admin-only on-demand trigger.
import { SendQualitySummaryTestButton } from '../components/quality/SendQualitySummaryTestButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NCRPODeepLinkChip, { usePODeepLink } from '../components/quality/NCRPODeepLinkChip';
import { useView } from '../contexts/ViewContext';
import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plus,
  Filter,
  X,
  Search,
} from 'lucide-react';
import {
  qualityAPI,
  platformSettingsAPI,
  fetchAllPages,
  NonConformanceReport,
  QualityTabsVisibility,
} from '../lib/api';
import { Pagination } from '../components/ui/pagination';
import { usePagination } from '../hooks';
import { getErrorMessage } from '../hooks/useFetchData';
import { useFormatters } from '../contexts/PreferencesContext';
import { SectionTourTrigger } from '../components/tour/SectionTourTrigger';
import { useIsCustomer } from '../hooks/usePortalMode';
import { toast } from 'sonner';

/**
 * CR-5887 rework: human labels for the Daily Quality Summary email deep-link
 * buckets, used by the active-filter banner so the user can see which row
 * they clicked. Keys mirror the ?next_action= / ?reject= URL values and the
 * email row wording exactly.
 */
/** Spec top-level states for the Status column funnel. */
const NCR_STATE_OPTIONS: { value: string; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'draft', label: 'Draft' },
];
const NEXT_ACTION_LABELS: Record<string, string> = {
  awaiting_supplier: 'Awaiting Supplier',
  awaiting_customer: 'Awaiting Customer',
  none: 'No Actions Outstanding',
};
const REJECT_LABELS: Record<string, string> = {
  pending: 'Reject requested — awaiting customer approval',
  declined: 'Reject requested — declined by customer',
  accepted: 'Reject requested — accepted by customer',
};

export default function Quality() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // CR-7304: opened from a PO header's NCR card — narrowed to that order.
  const poDeepLink = usePODeepLink();
  const isCustomer = useIsCustomer();
  const { viewKey, getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const { user } = useAuth();
  const { formatDate, formatNumber } = useFormatters();
  const [columns, setColumns] = useState<NCRColumnConfig[]>(loadNCRColumns);
  // 8D/A3 scoring master switch (Admin Setup > Features). When off, the
  // RC/CA/8D-A3 score columns are hidden from the register and the column
  // manager. Persistence is unaffected — loadNCRColumns always rebuilds the
  // full column set from DEFAULT_NCR_COLUMNS, so the columns return when the
  // feature is re-enabled.
  const { enable8dScoring, escapeRootCauseEnabled, ncrCustomerReferenceLabel } = useMenuSettings();
  const SCORING_COL_IDS = ['rc_score', 'erc_score', 'ca_score', 'epa_score', 'score_8d', 'close_off_days_8d'];
  const effectiveColumns = useMemo(
    () => {
      const scored = enable8dScoring ? columns : columns.filter((c) => !SCORING_COL_IDS.includes(c.id));
      // CR-7353: with the Escape chain off (Admin Setup → Quality) there is one
      // root cause and one corrective/preventive pair — no Escape columns, no
      // "Occurrence" wording. Applies to both the root-cause and the
      // corrective-action columns, mirroring the NCR form.
      if (escapeRootCauseEnabled) return scored;
      const OCCURRENCE_RELABEL: Record<string, string> = {
        selected_root_cause: 'Root Cause',
        corrective_action: 'Corrective Action',
        preventive_action: 'Preventive Action',
        // CR-7357 named the two existing grades ORC and OPA for DE's
        // four-dimension 8D. With the switch off there are two grades, so the
        // register says what it always said.
        rc_score: 'RC Score',
        ca_score: 'CA Score',
      };
      const ESCAPE_ONLY_IDS = [
        'escape_selected_root_cause',
        'escape_corrective_action',
        'escape_preventive_action',
        // The two Escape grades (CR-7357) are never scored with the switch off.
        'erc_score',
        'epa_score',
      ];
      return scored
        .filter((c) => !ESCAPE_ONLY_IDS.includes(c.id))
        .map((c) => (OCCURRENCE_RELABEL[c.id] ? { ...c, label: OCCURRENCE_RELABEL[c.id] } : c));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, enable8dScoring, escapeRootCauseEnabled],
  );
  const [ncrs, setNcrs] = useState<NonConformanceReport[]>([]);
  // E4: the customer's own name for its NCR number heads that column.
  const refLabel = useMemo(() => customerReferenceLabel(ncrs), [ncrs]);
  const visibleCols = useMemo(
    () => effectiveColumns.filter((c) => c.visible)
      .map((c) => (c.id === 'customer_reference' ? { ...c, label: refLabel } : c)),
    [effectiveColumns, refLabel],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('ncr_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // CR-5887: if the page is opened via a deep-link from the Daily
  // Quality Summary email, the filter panel is expanded by default so
  // the user can see why the list is scoped.
  // CR-5766: Also expand if supplier or liability param is present (from Performance KPI click-through)
  // CR-6310: Also expand if date_from/date_to params are present (from Performance tab drill-through)
  const [showFilters, setShowFilters] = useState(
    Boolean(searchParams.get('status') || searchParams.get('reject') || searchParams.get('next_action') || searchParams.get('supplier') || searchParams.get('liability') || searchParams.get('date_from') || searchParams.get('date_to'))
  );
  const [exporting, setExporting] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Filter states. CR-5887: seed from URL params so deep-links from the
  // Daily Quality Summary email land on the right filtered view.
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  const [rejectFilter, setRejectFilter] = useState(searchParams.get('reject') ?? '');
  // CR-5887 rework: the Daily Quality Summary email's "Awaiting Supplier /
  // Customer response" rows deep-link to ?next_action=<bucket>. Seed from the
  // URL so the list lands filtered to match the count the user clicked.
  const [nextActionFilter, setNextActionFilter] = useState(searchParams.get('next_action') ?? '');
  // QUALITY-NCR-REVIEW #17 (U2): the "from your Daily Quality Summary email"
  // banner is true only when the email's link (src=email) brought them here;
  // a chip click set the same filter and showed it too.
  const [fromEmail, setFromEmail] = useState(searchParams.get('src') === 'email');
  // Archived NCRs are out of every list by default; the Archived chip
  // sets this to 'true' to ask for them explicitly.
  const [archivedFilter, setArchivedFilter] = useState('');
  const [countsRefreshKey, setCountsRefreshKey] = useState(0);
  // Default the list to a rolling last-12-months window (the "From" date is
  // pre-filled, and the counts read the same value). Clearing it widens to
  // all-time. Deep-links from the Daily Quality Summary email keep all-time so
  // the email's count still equals the rows shown.
  // Shared with the filter panel's "Last 12 Months (Default)" chip so the
  // chip re-applies exactly the range the page loads with.
  const twelveMonthsAgoISO = () => getRollingTwelveMonthsRange().dateFrom;
  // CR-6310: Include date_from/date_to in deep-link check for Performance tab drill-through
  const hasDeepLinkFilter = Boolean(
    searchParams.get('status') || searchParams.get('reject') || searchParams.get('next_action') ||
    searchParams.get('date_from') || searchParams.get('date_to') || searchParams.get('po'),
  );
  // CR-6310: Seed date filters from URL params for Performance tab drill-through
  const [dateFrom, setDateFrom] = useState(
    searchParams.get('date_from') ?? (hasDeepLinkFilter ? '' : twelveMonthsAgoISO())
  );
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') ?? '');
  // CR-5550 round 2: surface the legacy Template-2 filter set in the UI.
  // Backend filters already exist (CR-5536 + CR-5550 round 1); these are
  // the controls that finally drive them.
  // CR-5766: Seed supplierFilter from URL param for Performance KPI click-through
  const [siteFilter, setSiteFilter] = useState('');
  const [supplierGroupFilter, setSupplierGroupFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState(searchParams.get('supplier') ?? '');
  const [commodityGroupFilter, setCommodityGroupFilter] = useState('');
  const [commodityTypeFilter, setCommodityTypeFilter] = useState('');
  const [strategicFilter, setStrategicFilter] = useState(false);
  // Priority-1 NCR list filters.
  const [partNumberFilter, setPartNumberFilter] = useState('');
  // Quality Engineer column filter. Backed by the supplier's quality-engineer
  // master data field, so it filters server-side across the whole dataset.
  const [qualityEngineerFilter, setQualityEngineerFilter] = useState('');
  // CR-7350: the Support Quality Engineer (CR-7345), alongside its sibling.
  // Both are now panel-owned, so both count towards the Filters badge and
  // both are cleared by Clear all; the column funnels write the same state.
  const [supportQualityEngineerFilter, setSupportQualityEngineerFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  // CR-5766: Seed liabilityFilter from URL param for Performance KPI click-through
  const [liabilityFilter, setLiabilityFilter] = useState(searchParams.get('liability') ?? '');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [claimStatusFilter, setClaimStatusFilter] = useState('');
  const [actionStatusFilter, setActionStatusFilter] = useState('');
  const [collectionDateFrom, setCollectionDateFrom] = useState('');
  const [collectionDateTo, setCollectionDateTo] = useState('');
  // CR-6444: cascade filter controls from the design.
  const [divisionFilter, setDivisionFilter] = useState('');
  const [companyCountryFilter, setCompanyCountryFilter] = useState('');
  const [supplierCodeFilter, setSupplierCodeFilter] = useState('');
  const [supplierTypeFilter, setSupplierTypeFilter] = useState('');
  // CR-6451: supplier-panel controls from the design.
  const [supplierCountryFilter, setSupplierCountryFilter] = useState('');
  const [srnFilter, setSrnFilter] = useState('');
  const [ncrIdFilter, setNcrIdFilter] = useState('');
  const [creditRequired, setCreditRequired] = useState(false);
  const [eightDRequired, setEightDRequired] = useState(false);
  const [collectionRequired, setCollectionRequired] = useState(false);
  const [replacementRequired, setReplacementRequired] = useState(false);

  // CR-7349: options for the Warehouse column funnel. Reported up by
  // NCRFiltersBar, which already loads them for its own Warehouse select —
  // so the funnel and the panel offer the same list off one request.
  const [warehouseOptions, setWarehouseOptions] = useState<Array<{ value: string; label: string }>>([]);

  // CR-6451: the filters the list is showing, as one value. The panel edits
  // a draft of this and sends it on Apply (useNCRFilterDraft); everything
  // else — chips, column funnels, deep links — still writes the applied
  // state directly and the draft re-seeds from it.
  const appliedValues: NCRFiltersState = {
    searchTerm, statusFilter, dateFrom, dateTo, siteFilter, supplierGroupFilter,
    supplierFilter, commodityGroupFilter, commodityTypeFilter, strategicFilter,
    partNumberFilter, regionFilter, liabilityFilter, warehouseFilter,
    qualityEngineerFilter, supportQualityEngineerFilter,
    claimStatusFilter, actionStatusFilter, collectionDateFrom, collectionDateTo,
    divisionFilter, companyCountryFilter, supplierCodeFilter, supplierTypeFilter,
    supplierCountryFilter, srnFilter, ncrIdFilter, creditRequired, eightDRequired,
    collectionRequired, replacementRequired,
  };
  const { draft, patchDraft } = useNCRFilterDraft(appliedValues);

  // Phase 1 Excel-style per-column filters. Each entry reuses an existing
  // server-side NCR filter (NCRFilterSet) so the funnel in a column header
  // filters the whole dataset, not just the visible page — and stays in sync
  // with NCRFiltersBar because both write the same state.
  const columnFilters: Record<string, ColumnFilterDescriptor> = {
    status_display: { type: 'choice', value: statusFilter, onChange: setStatusFilter, options: NCR_STATUS_OPTIONS, allLabel: 'All Statuses' },
    ncr_state: { type: 'choice', value: statusFilter, onChange: setStatusFilter, options: NCR_STATE_OPTIONS, allLabel: 'All' },
    liability_display: { type: 'choice', value: liabilityFilter, onChange: setLiabilityFilter, options: LIABILITY_OPTIONS, allLabel: 'All Liabilities' },
    is_credit_required: { type: 'choice', value: claimStatusFilter, onChange: setClaimStatusFilter, options: CLAIM_STATUS_OPTIONS, allLabel: 'All Claim Statuses' },
    part_number: { type: 'text', value: partNumberFilter, onChange: setPartNumberFilter },
    supplier_quality_engineer: { type: 'text', value: qualityEngineerFilter, onChange: setQualityEngineerFilter },
    supplier_support_quality_engineer: { type: 'text', value: supportQualityEngineerFilter, onChange: setSupportQualityEngineerFilter },
    // CR-7349: reuses the panel's own warehouse filter, so funnelling a
    // warehouse from the column header and picking it in the panel are the
    // same action and show the same chip.
    warehouse_display: { type: 'choice', value: warehouseFilter, onChange: setWarehouseFilter, options: warehouseOptions, allLabel: 'All Warehouses' },
  };

  // Quality tab visibility from platform settings
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });
  const [tabSettingsLoaded, setTabSettingsLoaded] = useState(false);

  // CR-6385: the tab needs BOTH the admin's tab-visibility choice and the
  // concession feature switch. The first is layout, the second is whether the
  // tenant runs concessions at all — a tenant that never opted in must not be
  // one settings toggle away from the workflow.
  // Sourced from this page's own settings fetch, not MenuSettingsContext: the
  // context loads separately and starts false, so reading the two together
  // raced and could redirect a tenant away from a tab it is entitled to.
  const [concessionsEnabled, setConcessionsEnabled] = useState(false);
  const showConcessionsTab = tabVisibility.concessions && concessionsEnabled;

  // CR-7352: backlog on the My Collections tab.
  const { count: myCollectionsCount } = useMyCollectionsCount(isCustomer);

  const pagination = usePagination({
    pageSize: 15,
    onPageChange: () => {}, // Fetch is triggered by useEffect
  });

  // Debounce search input - wait 500ms after user stops typing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      pagination.reset();
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Map frontend display fields to backend ordering fields
  const fieldMapping: Record<string, string> = {
    id: 'id',
    company_name: 'company__name',
    ncr_number: 'ncr_number',
    supplier_name: 'supplier__name',
    part_number: 'part_number',
    grn_number: 'grn_number',
    ncr_date: 'ncr_date',
    status: 'status',
  };

  type SortableField = keyof typeof fieldMapping;

  const handleSort = (field: SortableField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    pagination.reset();
  };

  const getSortIcon = (field: SortableField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  // QUALITY-NCR-REVIEW #17: the ONE set of filters the list shows. The
  // export and the chip counts used to rebuild their own partial copies, so
  // with a panel filter applied they exported and counted a different set
  // from the rows on screen.
  const listFilterParams = (): Record<string, string | number | boolean | undefined> => ({
    search: debouncedSearchTerm || undefined,
    // CR-5887: the Daily Quality Summary email's buckets.
    reject: rejectFilter || undefined,
    next_action: nextActionFilter || undefined,
    // Archived NCRs are hidden unless the Archived chip is active.
    archived: archivedFilter || undefined,
    // CR-6451: every panel-owned filter goes through one mapping, which the
    // ratchet gate asserts key by key against the backend FilterSet.
    ...buildNCRPanelParams(appliedValues),
    // CR-6794: the Quality Engineer column funnel is page-owned.
    quality_engineer: qualityEngineerFilter || undefined,
    // CR-7345/CR-7350 (merged 30 Sep): the Support Quality Engineer funnel.
    support_quality_engineer: supportQualityEngineerFilter || undefined,
    po: poDeepLink.poId || undefined,
    ...getViewQueryParams(),
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const backendField = fieldMapping[sortField] || sortField;
      const ordering = sortDirection === 'desc' ? `-${backendField}` : backendField;
      const ncrsResponse = await qualityAPI.getNCRs({
        ordering,
        page: pagination.currentPage,
        page_size: pagination.pageSize,
        ...listFilterParams(),
      });

      setNcrs(ncrsResponse.data.results);
      pagination.setFromResponse(ncrsResponse.data.count, (ncrsResponse.data.results || []).length);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load quality data'));
    } finally {
      setLoading(false);
    }
  // CR-6444: divisionFilter, companyCountryFilter, supplierCodeFilter and
  // supplierTypeFilter are in appliedValues (read inside via
  // buildNCRPanelParams) but were missing here, so applying only one of them
  // recreated nothing and no request went out — the list stayed put while
  // the badge said a filter was on.
  }, [pagination.currentPage, debouncedSearchTerm, sortField, sortDirection, statusFilter, rejectFilter, nextActionFilter, archivedFilter, dateFrom, dateTo, siteFilter, supplierGroupFilter, supplierFilter, commodityGroupFilter, commodityTypeFilter, strategicFilter, partNumberFilter, qualityEngineerFilter, supportQualityEngineerFilter, regionFilter, liabilityFilter, warehouseFilter, claimStatusFilter, actionStatusFilter, collectionDateFrom, collectionDateTo, divisionFilter, companyCountryFilter, supplierCodeFilter, supplierTypeFilter, supplierCountryFilter, srnFilter, ncrIdFilter, creditRequired, eightDRequired, collectionRequired, replacementRequired, poDeepLink.poId, isCustomer, viewKey, getViewQueryParams]);

  useEffect(() => {
    if (viewContextLoading) return;
    fetchData();
  }, [fetchData, viewContextLoading, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  // Fetch platform settings for NCR template preference and tab visibility
  useEffect(() => {
    const fetchPlatformSettings = async () => {
      try {
        // Fetch display settings for tab visibility
        const displayResponse = await platformSettingsAPI.getDisplaySettings();
        if (displayResponse.data?.quality_tabs) {
          setTabVisibility(prev => ({ ...prev, ...displayResponse.data.quality_tabs }));
        }
        // CR-6385: concession master switch, from the same payload.
        if (displayResponse.data) {
          setConcessionsEnabled(!!displayResponse.data.concessions_enabled);
        }

        // 24 Sep 2026: the NCR template switch is retired -- one workflow for
        // everyone (Aaron: "scrap it and build a proper one"). The list no
        // longer reads platform settings for it.
      } catch (error) {
        console.error('Error fetching platform settings:', error);
      } finally {
        setTabSettingsLoaded(true);
      }
    };
    fetchPlatformSettings();
  }, []);

  // Redirect if NCRs tab is disabled - navigate to first available quality tab
  useEffect(() => {
    if (!tabSettingsLoaded) return;

    if (!tabVisibility.ncrs) {
      // NCRs disabled - redirect to first available tab
      if (showConcessionsTab) {
        navigate('/concessions', { replace: true });
      } else if (tabVisibility.approval_register && isCustomer) {
        navigate('/intelligence/qualifications', { replace: true });
      }
      // If no tabs are available, stay on page but content won't show
    }
  }, [tabSettingsLoaded, tabVisibility, showConcessionsTab, navigate, isCustomer]);

  const handleViewNCR = (ncrId: number) => {
    // CR-6444: open NCR details in new tab per client request
    window.open(`/quality/ncr/${ncrId}/edit`, '_blank');
  };

  const handleAddNCR = () => {
    navigate('/quality/ncr/new');
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);

      // Fetch all NCRs (no pagination) for export
      const backendField = fieldMapping[sortField] || sortField;
      const ordering = sortDirection === 'desc' ? `-${backendField}` : backendField;

      // Walk every page — the backend clamps page_size, so a single large
      // request silently exported at most one page of the filtered set.
      const filters = listFilterParams();
      const exportData = await fetchAllPages<NonConformanceReport>((page) => qualityAPI.getNCRs({
        ordering,
        page,
        page_size: 1000,
        ...filters,
      }));

      if (exportData.length === 0) {
        toast.error('No data to export');
        return;
      }

      // CR-5535: extended column set to match legacy platform export.
      // Added 13 columns Sarah called out as missing: Supplier Code,
      // Buyer, PO Line No, Part Description, Fault Group, Fault Category,
      // Source of Rejection, Response By, Completed By, Selected Root
      // Cause, Corrective Action, Preventive Action, Packing Number No.
      // Underlying fields are now exposed by NonConformanceReportListSerializer.
      //
      // CR-5535 rework (issues 5 & 6): the "Response By" and "Completed
      // By" columns in the legacy SharePoint export were misleadingly
      // labelled — they actually carried CARequiredDate and
      // FRRequiredDate values (see legacy NetworkSQL.cs
      // VIEW_NCRS_NCRLIST_BY_EXCEL: `detail.CARequiredDate ResponseBy,
      // detail.FRRequiredDate CompletedBy`). The Helix backend now
      // computes ca_required_date / car_required_date as
      // raised_date + working-day leadtime so the export columns match
      // what legacy customers saw historically.
      const headers = [
        // Was 'NCR ID' carrying the database primary key — an internal value,
        // unstable across a restore and meaningless in the customer's own
        // systems. Exports the NCR number instead. The separate
        // 'Company NCR/SRN No.' column is kept so the export shape legacy
        // customers match against does not change.
        'NCR No.',
        'Customer',
        customerReferenceLabel(exportData),
        'Supplier',
        'Supplier Code',
        'Buyer',
        'Part No.',
        'PO Line No.',
        'Part Description',
        'GRN No.',
        'PO No.',
        'Raised By',
        'NCR Date',
        'Status',
        'Severity',
        'Fault Group',
        'Fault Category',
        'Source of Rejection',
        'Response By',
        'Completed By',
        'Defect Description',
        'Batch Qty',
        'Reject Qty',
        'Shortage Qty',
        'Selected Root Cause',
        'Corrective Action',
        'Preventive Action',
        'Packing Number No.',
        // CR-7345: appended (not inserted) so the legacy column positions
        // customers match against do not shift. Quality Engineer joins its
        // sibling here — it was list-only before, now both export.
        'Quality Engineer',
        'Support Quality Engineer',
        // CR-7349: the receiving warehouse, resolved server-side through the
        // same chain the warehouse filter matches on.
        'Warehouse',
        // CR-7353: appended, and only while the Escape chain is on.
        ...(escapeRootCauseEnabled
          ? ['Escape Root Cause', 'Escape Corrective Action', 'Escape Preventive Action']
          : []),
        // CR-7357/CR-7358: the 8D grades and their total, appended so the
        // legacy column positions still do not shift. They follow the same two
        // switches as the register columns — the 8D/A3 master switch, and the
        // Escape chain for the two Escape grades — so the export never carries
        // a column the register cannot show.
        ...(enable8dScoring
          ? [
              escapeRootCauseEnabled ? 'ORC Score' : 'RC Score',
              ...(escapeRootCauseEnabled ? ['ERC Score'] : []),
              escapeRootCauseEnabled ? 'OPA Score' : 'PA Score',
              ...(escapeRootCauseEnabled ? ['EPA Score'] : []),
              '8D/A3 Score',
            ]
          : []),
      ];

      // The button says "Export to Excel", so write a real .xlsx (same
      // SheetJS path Supplier Performance uses) rather than a CSV with an
      // Excel label. Cells are plain values — no CSV quoting needed.
      const text = (v: unknown): string => String(v ?? '').replace(/\n/g, ' ');

      const rows = exportData.map((ncr) => {
        const values: (string | number)[] = [
          text(ncr.ncr_number),
          text(ncr.customer_name || ncr.company_name),
          // B3: the customer's own number; for an older NCR whose number was
          // typed over, that typed number (blank when system-issued).
          text(ncr.customer_reference) || companyNcrReference(ncr.ncr_number),
          text(ncr.supplier_name),
          text(ncr.supplier_code),
          text(ncr.buyer_name),
          text(ncr.part_number),
          ncr.po_line_number ?? '',
          text(ncr.part_description),
          text(ncr.grn_number),
          text(ncr.po_number),
          text(ncr.raised_by_name),
          ncr.ncr_date ? formatDate(ncr.ncr_date) : '',
          ncr.status_display || ncr.status,
          ncr.severity_display || capitaliseSeverity(ncr.severity),
          text(ncr.fault_group_name),
          text(ncr.fault_category_name),
          text(ncr.source_of_rejection_display || ncr.source_of_rejection),
          ncr.ca_required_date ? formatDate(ncr.ca_required_date) : '',
          ncr.car_required_date ? formatDate(ncr.car_required_date) : '',
          text(ncr.defect_description),
          ncr.batch_qty || 0,
          ncr.reject_qty || 0,
          ncr.shortage_qty || 0,
          text(ncr.selected_root_cause),
          text(ncr.corrective_action_summary),
          text(ncr.preventive_action),
          text(ncr.packing_note_no),
          text(ncr.supplier_quality_engineer),
          text(ncr.supplier_support_quality_engineer),
          text(ncr.warehouse_display),
          ...(escapeRootCauseEnabled
            ? [
                text(ncr.escape_selected_root_cause),
                text(ncr.escape_corrective_action),
                text(ncr.escape_preventive_action),
              ]
            : []),
          // Unscored dimensions export blank rather than 0 — 0 is not a valid
          // grade and would read as the worst one.
          ...(enable8dScoring
            ? [
                ncr.rc_score ?? '',
                ...(escapeRootCauseEnabled ? [ncr.erc_score ?? ''] : []),
                ncr.ca_score ?? '',
                ...(escapeRootCauseEnabled ? [ncr.epa_score ?? ''] : []),
                ncr.score_8d ?? '',
              ]
            : []),
        ];
        return values;
      });

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'NCRs');
      XLSX.writeFile(workbook, `NCR_Export_${new Date().toISOString().split('T')[0]}.xlsx`);

      toast.success(`Exported ${exportData.length} NCRs to Excel`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export NCRs');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setFromEmail(false);
    setStatusFilter('');
    setRejectFilter('');
    setNextActionFilter('');
    setDateFrom('');
    setDateTo('');
    setSearchTerm('');
    setDebouncedSearchTerm('');
    // CR-5550 round 2
    setSiteFilter('');
    setSupplierGroupFilter('');
    setSupplierFilter('');
    setCommodityGroupFilter('');
    setCommodityTypeFilter('');
    setStrategicFilter(false);
    // Priority-1 list filters.
    setPartNumberFilter('');
    setQualityEngineerFilter('');
    setSupportQualityEngineerFilter('');  // CR-7350
    setRegionFilter('');
    setLiabilityFilter('');
    setWarehouseFilter('');
    setClaimStatusFilter('');
    setActionStatusFilter('');
    setCollectionDateFrom('');
    setCollectionDateTo('');
    // CR-6444
    setDivisionFilter('');
    setCompanyCountryFilter('');
    setSupplierCodeFilter('');
    setSupplierTypeFilter('');
    // CR-6451
    setSupplierCountryFilter('');
    setSrnFilter('');
    setNcrIdFilter('');
    setCreditRequired(false);
    setEightDRequired(false);
    setCollectionRequired(false);
    setReplacementRequired(false);
    // CR-5887: also drop the URL params so a refresh doesn't re-apply them.
    setSearchParams({});
    pagination.reset();
  };

  /**
   * CR-6451: Apply filters — push the panel's draft into the applied state
   * in one go. Every key is written (cheap, and it keeps the two in step);
   * the page resets to page 1 once.
   */
  const applyPanelValues = (next: NCRFiltersState) => {
    setSearchTerm(next.searchTerm);
    setStatusFilter(next.statusFilter);
    setDateFrom(next.dateFrom);
    setDateTo(next.dateTo);
    setSiteFilter(next.siteFilter);
    setSupplierGroupFilter(next.supplierGroupFilter);
    setSupplierFilter(next.supplierFilter);
    setCommodityGroupFilter(next.commodityGroupFilter);
    setCommodityTypeFilter(next.commodityTypeFilter);
    setStrategicFilter(next.strategicFilter);
    setPartNumberFilter(next.partNumberFilter);
    setRegionFilter(next.regionFilter);
    setLiabilityFilter(next.liabilityFilter);
    setWarehouseFilter(next.warehouseFilter);
    setClaimStatusFilter(next.claimStatusFilter);
    setActionStatusFilter(next.actionStatusFilter);
    setCollectionDateFrom(next.collectionDateFrom);
    setCollectionDateTo(next.collectionDateTo);
    setDivisionFilter(next.divisionFilter);
    setCompanyCountryFilter(next.companyCountryFilter);
    setSupplierCodeFilter(next.supplierCodeFilter);
    setQualityEngineerFilter(next.qualityEngineerFilter);            // CR-7350
    setSupportQualityEngineerFilter(next.supportQualityEngineerFilter);
    setSupplierTypeFilter(next.supplierTypeFilter);
    setSupplierCountryFilter(next.supplierCountryFilter);
    setSrnFilter(next.srnFilter);
    setNcrIdFilter(next.ncrIdFilter);
    setCreditRequired(next.creditRequired);
    setEightDRequired(next.eightDRequired);
    setCollectionRequired(next.collectionRequired);
    setReplacementRequired(next.replacementRequired);
    pagination.reset();
  };

  // CR-6444: badge count updates immediately as user selects filters (uses draft,
  // not appliedValues). The rolling 12-month default is not a filter the user
  // chose, so it is not counted.
  const activeFilterCount = countActiveNCRFilters(draft, twelveMonthsAgoISO());

  const getStatusBadge = (status: string, statusDisplay: string) => {
    // Match old SupplierPortal styling
    let badgeClass = '';

    if (status === 'closed') {
      badgeClass = 'bg-[#d4edda] text-[#155724] border-[#28a745]';
    } else if (status === 'deleted') {
      badgeClass = 'bg-[#f8d7da] text-[#721c24] border-[#dc3545]';
    } else {
      // Open statuses (draft, created, ca_submitted, car_submitted)
      badgeClass = 'bg-[#e9ecef] text-[#495057] border-[#6c757d]';
    }

    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${badgeClass}`}>
        {statusDisplay}
      </span>
    );
  };

  // Show loading while checking tab settings
  if (!tabSettingsLoaded || (loading && !ncrs.length)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If NCRs tab is disabled, show access denied (redirect should have happened)
  if (!tabVisibility.ncrs) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">This section is not available. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
          <button
            onClick={fetchData}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Quick-filter chips (All / Awaiting Supplier / Awaiting Customer / Appeals /
  // Closed) drive the same status/next_action/reject params as the detail
  // panel. Derive the active chip from the current values, and apply a chip by
  // resetting all three mode params to that chip's set.
  const activeQuickMode: NCRQuickMode =
    archivedFilter === 'true' ? 'archived'
    : rejectFilter === 'pending' ? 'appeals'
    : nextActionFilter === 'awaiting_supplier' ? 'awaiting_supplier'
    : nextActionFilter === 'awaiting_customer' ? 'awaiting_customer'
    : nextActionFilter === 'none' ? 'no_actions'
    : statusFilter === 'closed' ? 'closed'
    : (!statusFilter && !rejectFilter && !nextActionFilter) ? 'all'
    : 'all';

  // Archive/unarchive and delete are customer-admin actions. `is_company_admin`
  // is the user API's view of `profile.role.is_admin_role` (same derivation as
  // lib/newsAccess). Helix internals count too — they operate every tenant.
  const isCustomerAdmin = Boolean(
    user && (
      user.company_type === 'helix'
      || (user.company_type === 'customer' && user.is_company_admin)
    ),
  );

  // Archiving or deleting changes both the page of rows and every chip count,
  // and neither refetches on its own: the list keys off the filter params and
  // the counts off a JSON key of the same params, none of which move when a
  // row's own state changes. Bumping this drives both.
  const handleRowActionDone = () => {
    fetchData();
    setCountsRefreshKey((k) => k + 1);
  };

  const applyQuickMode = (mode: NCRQuickMode) => {
    const params = NCR_QUICK_MODES.find((m) => m.key === mode)?.params ?? {};
    setFromEmail(false);
    setStatusFilter(params['status'] ?? '');
    setRejectFilter(params['reject'] ?? '');
    setNextActionFilter(params['next_action'] ?? '');
    setArchivedFilter(params['archived'] ?? '');
    pagination.reset();
  };

  // Base params for the chip counts — everything except the mode keys.
  // The chips' counts use the list's own filters minus the four each chip
  // sets itself (QUALITY-NCR-REVIEW #17).
  const quickBaseParams: Record<string, unknown> = (() => {
    const { status: _s, ncr_state: _n, reject: _r, next_action: _a, archived: _x, ...rest } = listFilterParams();
    return rest;
  })();

  return (
    <div className="space-y-4">
      {/* Section Tour Trigger */}
      <div className="flex justify-end mb-4">
        <SectionTourTrigger
          pageKey="quality"
          sectionKey="ncrs"
          variant="link"
          label="Tour this page"
        />
      </div>

      {/* Tab bar — its own card, as on Purchasing */}
      <Card className="rounded-lg shadow-sm mb-4">
        <CardHeader className="border-b bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <QualityTabStrip
              active="ncrs"
              tabVisibility={tabVisibility}
              concessionsEnabled={concessionsEnabled}
              isCustomer={isCustomer}
              myCollectionsCount={myCollectionsCount}
            />
          </div>
        </CardHeader>
      </Card>

      {/* CR-6451: Filter panel — matches the Deliveries tab. Always mounted so
          its header (Refresh / Export / Filters) stays visible when collapsed;
          only the sections and the footer hide. The panel edits a draft and
          sends it on Apply filters. */}
      <div className="mb-4">
        <NCRFiltersBar
          customerReferenceLabel={ncrCustomerReferenceLabel}
          values={draft}
          onChange={patchDraft}
          onApply={() => applyPanelValues(draft)}
          onClear={clearFilters}
          expanded={showFilters}
          onShowFilters={() => setShowFilters(true)}
          onHideFilters={() => setShowFilters(false)}
          onRefresh={handleRowActionDone}
          onExport={handleExportExcel}
          exporting={exporting}
          activeFilterCount={activeFilterCount}
          supplierLayout={!isCustomer}
          onWarehouseOptionsLoaded={(list) => setWarehouseOptions(
            list.map((w) => ({ value: String(w.id), label: w.code ? `${w.code} - ${w.name}` : w.name })),
          )}
          // CR-6451: the supplier design omits the strategic-suppliers
          // tickbox - a supplier filtering on that is filtering on
          // themselves.
          showStrategicFilter={isCustomer}
        />
      </div>

      {/* Results */}
      <Card className="rounded-lg shadow-sm" data-tour="ncr-table">
        <CardContent className="p-0">
          {/* View bar — count on the left (with ADD for customers; suppliers
              cannot raise NCRs), column manager on the right. */}
          <div className="border-b bg-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">
                  Showing {ncrs.length} of {pagination.totalCount} NCRs
                </span>
                {isCustomer && (
                  <Button
                    onClick={handleAddNCR}
                    className="bg-[#1bc5bd] hover:bg-[#0bb4ac] text-white font-semibold uppercase text-sm px-6 py-2"
                    data-tour="add-ncr-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    ADD
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-3">
                {/* CR-5887 (TEMPORARY - remove after testing): Helix-admin only. */}
                <SendQualitySummaryTestButton />
                <QualityColumnsDropdown columns={effectiveColumns} onChange={setColumns} />
              </div>
            </div>
          </div>

          {/* Quick-filter chips - key modes only, incl. Appeals (supplier
              rejection requested, awaiting the customer's decision). */}
          <div className="px-4 pb-3 pt-1 border-b">
            <NCRPODeepLinkChip deepLink={poDeepLink} onClear={() => { poDeepLink.clear(); pagination.reset(); }} />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <NCRQuickFilters
                activeMode={activeQuickMode}
                onSelect={applyQuickMode}
                isCustomerAdmin={isCustomerAdmin}
                baseParams={quickBaseParams}
                refreshKey={countsRefreshKey}
              />
              {/* QUALITY-NCR-REVIEW #17: the list's search had no box. It
                  searches NCR No., part, descriptions, supplier and customer. */}
              <div className="relative w-full sm:w-72">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search NCR No., part, supplier..."
                  aria-label="Search NCRs"
                  className="h-9 pl-8"
                />
              </div>
            </div>
          </div>

          {/* CR-5887 rework: active deep-link filter banner. When the user
              arrives from a Daily Quality Summary email link, show exactly
              which bucket the list is scoped to (and let them clear it). */}
          {fromEmail && (nextActionFilter || rejectFilter) && (
            <div className="px-6 pt-4">
              <div className="flex items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-800">
                <span className="flex flex-wrap items-center gap-1">
                  <Filter className="mr-1 h-4 w-4 flex-shrink-0" />
                  {nextActionFilter ? (
                    <>Filtered to <strong>Next Action: {NEXT_ACTION_LABELS[nextActionFilter] ?? nextActionFilter}</strong></>
                  ) : (
                    <>Filtered to <strong>{REJECT_LABELS[rejectFilter] ?? rejectFilter}</strong></>
                  )}
                  <span className="text-blue-600">— from your Daily Quality Summary email.</span>
                </span>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="flex flex-shrink-0 items-center gap-1 font-medium text-blue-700 hover:text-blue-900"
                >
                  <X className="h-4 w-4" /> Clear
                </button>
              </div>
            </div>
          )}

          {/* Data Table Section */}
          <div className="p-6">
            {ncrs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {searchTerm || statusFilter ? 'No NCRs found matching your criteria' : 'No NCRs found'}
              </div>
            ) : (
              <>
                {/* Top Pagination */}
                <div className="mb-4">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalCount={pagination.totalCount}
                    itemsPerPage={pagination.pageSize}
                    onPageChange={pagination.goToPage}
                    itemLabel="NCRs"
                  />
                </div>

                <div className="mb-2 flex justify-end"><PlateLegend /></div>

                {/* Table — columns + order driven by qualityColumnConfig (CR-DE-Quality-Cols).
                    CR-5547: switch to internal scrollable container with sticky
                    headers, mirroring the PO Line Items table layout, so column
                    headers stay visible during vertical scroll and horizontal
                    scrolling stays inside the table rather than the whole page.
                    Height budget matches POLinesTable (calc(100vh - 280px)). */}
                <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)] border rounded-lg">
                  <table className="w-full">
                    <thead className="sticky top-0 z-20">
                      <tr className="bg-[#f3f6f9] border-b">
                        {visibleCols.map((col) => {
                          const sortKey = col.sortField || col.id;
                          return (
                            <th
                              key={col.id}
                              style={{ minWidth: col.minWidth }}
                              className={`text-left py-3 px-4 font-semibold text-sm text-[#3f4254] select-none ${
                                col.sortable ? 'cursor-pointer hover:bg-[#ebedf3]' : ''
                              }`}
                              onClick={col.sortable ? () => handleSort(sortKey) : undefined}
                            >
                              <div className="flex items-center">
                                {col.label}
                                {col.sortable && getSortIcon(sortKey)}
                                {columnFilters[col.id] && (
                                  <ColumnHeaderFilter label={col.label} {...columnFilters[col.id]} />
                                )}
                              </div>
                            </th>
                          );
                        })}
                        {isCustomerAdmin && (
                          <th className="text-right py-3 px-4 font-semibold text-sm text-[#3f4254] w-[60px]">
                            <span className="sr-only">Actions</span>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {ncrs.map((ncr, index) => (
                        <tr
                          key={ncr.id}
                          className={`border-b cursor-pointer transition-colors hover:bg-[#f3f6f9] ${
                            index % 2 === 0 ? 'bg-white' : 'bg-[#f9fafb]'
                          }`}
                          style={{ height: '58px' }}
                          onClick={() => handleViewNCR(ncr.id)}
                        >
                          {visibleCols.map((col) => (
                            <td
                              key={col.id}
                              className={`py-3 px-4 text-sm text-[#3f4254] ${
                                col.id === 'part_number' ? 'font-mono' : ''
                              }`}
                            >
                              {col.id === 'status_display'
                                ? getStatusBadge(ncr.status, ncr.status_display)
                                : renderNCRCell(col, ncr, { formatDate, formatNumber })}
                            </td>
                          ))}
                          {isCustomerAdmin && (
                            <td className="py-3 px-4 text-right">
                              <NCRRowActions ncr={ncr} onChanged={handleRowActionDone} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Bottom Pagination */}
                <div className="mt-4">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalCount={pagination.totalCount}
                    itemsPerPage={pagination.pageSize}
                    onPageChange={pagination.goToPage}
                    itemLabel="NCRs"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
